// The job: the pod, its place on the route, the quality checks, and the gate
// that decides whether it can move on.
//
// Opening a pod is a look; starting it is a separate, deliberate tap that puts
// the operator's name on the clock in Odoo. The footer's one big button is
// whichever of those is next, and it changes colour the moment the job begins.

import { useEffect, useMemo, useState } from 'react';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { CheckCard } from '@/components/checks/CheckCard';
import { JourneyProgress, isActionable, operatorMayStart, stateLabel } from '@/components/pod/PodParts';
import { RouteSheet } from '@/components/pod/RouteSheet';
import { WaitingOn } from '@/components/pod/WaitingOn';
import { useT } from '@/i18n/useT';
import { fmtElapsed, useElapsedSeconds } from '@/hooks/useElapsed';
import { checksResolved, useCheckAction, useChecks, useJourney, usePodAlerts, useWoLive, type CheckAction } from '@/queries';
import type { JourneyStepVM, PodVM } from '@/api/types';
import { useStore } from '@/store';

export function JobScreen({
  pod,
  tablet,
  backLabel,
  starting,
  onBack,
  onStart,
  onPause,
  onComplete,
  onCapturePhoto,
  pendingPhoto,
  onConfirmPhoto,
  onOpenDocs,
  onOpenIssues,
  onRaise,
  onJumpToStep,
}: {
  pod: PodVM;
  tablet: boolean;
  /** Where Back goes: the pods list or the station. */
  backLabel: string;
  starting: boolean;
  onBack: () => void;
  onStart: () => void;
  onPause: () => void;
  onComplete: () => void;
  onCapturePhoto: (checkId: number) => void;
  /** A frame captured but not yet written, with the check it belongs to. */
  pendingPhoto?: { checkId: number; base64: string } | null;
  onConfirmPhoto?: () => void;
  onOpenDocs: () => void;
  onOpenIssues: () => void;
  /** Raise an issue with this pod already filled in. */
  onRaise: () => void;
  /** Follow a blocking step to the work centre that owns it. */
  onJumpToStep?: (step: JourneyStepVM) => void;
}) {
  const t = useT();
  const openCheck = useStore((s) => s.openCheck);
  const toggleCheck = useStore((s) => s.toggleCheck);
  const readSet = useStore((s) => s.readSet);
  const markRead = useStore((s) => s.markRead);
  const pendingCheckIds = useStore((s) => s.pendingCheckIds);
  const setCheckPending = useStore((s) => s.setCheckPending);
  const pauseReason = useStore((s) => s.pauseReason);
  const wcName = useStore((s) => s.workcentreName);

  const [showRoute, setShowRoute] = useState(false);
  const checks = useChecks(pod.woId);
  const live = useWoLive(pod.woId);
  const journey = useJourney(pod.moId);
  const alerts = usePodAlerts(pod.moId);
  const nextStep = (() => {
    const steps = journey.data ?? [];
    const here = steps.findIndex((s) => s.woId === pod.woId);
    if (here < 0) return null;
    return steps.slice(here + 1).find((s) => s.status !== 'done')?.workcentreName ?? null;
  })();
  const elapsed = useElapsedSeconds(live.data);
  const action = useCheckAction(pod.woId);

  const list = useMemo(() => checks.data ?? [], [checks.data]);
  const progress = checksResolved(checks.data);
  const running = !!live.data?.startedAt;
  // "Started" means Odoo has a time record for it, not that we navigated here.
  const started = running || (live.data?.priorDurationSec ?? 0) > 0 || pod.state === 'progress';
  const operatorId = useStore((s) => s.operator?.id ?? null);
  const connMode = useStore((s) => s.connMode);
  // The roster binds the kiosk's PIN operators; a named account is let
  // through everywhere (interpod_mrp_shopfloor's gate says the same).
  const permitted = connMode === 'user' || operatorMayStart(pod, operatorId);
  const canStart = isActionable(pod.state) && permitted;
  /**
   * This step is over at this station — signed off, or cancelled. Reachable
   * because the finder searches pods in any state; there is nothing to start
   * and nothing to complete, and what it still has to offer is its route.
   */
  const settled = pod.state === 'done' || pod.state === 'cancel';
  /**
   * Not at this station yet. The checks mean nothing until it arrives, so
   * the screen answers the question the operator actually has instead: what
   * is holding it, and where. The native shop floor app shows the unfinished
   * predecessors the moment a pod is scanned, and asking for a tap to see
   * them here was a step backwards the floor noticed.
   */
  const upstream = !settled && !started && !isActionable(pod.state);

  // Open the first unresolved check, and advance as each is signed off.
  useEffect(() => {
    if (!list.length) return;
    const stillOpen = list.find((c) => c.id === openCheck);
    if (stillOpen && stillOpen.state === 'none') return;
    const next = list.find((c) => c.state === 'none');
    toggleCheck(next ? next.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  /**
   * The Done button on a failed check: not a sign-off, a pointer. The server
   * refuses button_finish while any check is 'fail' (naming them in its
   * error — still the backstop should this slip), so the tap opens the first
   * failed card and brings it into view, where Pass is.
   */
  const goToFirstFailed = () => {
    const first = list.find((c) => c.state === 'fail');
    if (!first) return;
    if (openCheck !== first.id) toggleCheck(first.id);
    document.getElementById(`check-${first.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const runAction = (checkId: number, a: CheckAction) => {
    const check = list.find((c) => c.id === checkId);
    if (!check) return;
    setCheckPending(checkId, true);
    action.mutate({ check, action: a }, { onSettled: () => setCheckPending(checkId, false) });
  };

  const issueCount = alerts.data?.length ?? (pod.hasOpenAlerts ? 1 : 0);
  const chips = [
    pod.flowlineName?.replace(/^Flowline\s*/i, 'FL '),
    `WO: ${stateLabel(pod.state)}`,
    pod.quarantine ? 'Quarantine' : null,
    ...pod.spec,
  ].filter((c): c is string => !!c);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, animation: 'ipRise .3s', flex: 1, minHeight: 0 }}>
      <h1 className="sr-only">
        {pod.woName} on {pod.mo}
      </h1>

      <button
        type="button"
        className="press"
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          minHeight: 36,
          padding: '0 12px',
          borderRadius: 10,
          background: 'var(--surface2)',
          color: 'var(--sub)',
          font: '600 11.5px Manrope,sans-serif',
          alignSelf: 'flex-start',
        }}
      >
        <Icon name="back" size={13} />
        {t.backToT} {backLabel}
      </button>

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 220 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ font: "600 13px 'Space Grotesk',sans-serif", color: 'var(--sub)' }}>{pod.mo}</span>
            {chips.map((c) => (
              <span
                key={c}
                style={{ borderRadius: 7, padding: '2px 8px', font: '600 10.5px Manrope,sans-serif', background: 'var(--surface2)', color: c === 'Quarantine' ? 'var(--danger)' : 'var(--sub)' }}
              >
                {c}
              </span>
            ))}
          </span>
          <span style={{ display: 'block', font: "700 24px/1.1 'Space Grotesk',sans-serif", marginTop: 6 }}>
            {pod.proj || pod.productName}
          </span>
          <span style={{ display: 'block', font: '500 12px Manrope,sans-serif', color: 'var(--sub)', marginTop: 4 }}>
            {[wcName, pod.type, pod.est ? `${t.estimated} ${pod.est}` : null, pod.woName].filter(Boolean).join(' · ')}
          </span>
        </span>
        <button
          type="button"
          className="press"
          onClick={started && !settled ? onPause : undefined}
          disabled={!started || settled}
          style={{ textAlign: 'right', flex: 'none', minHeight: 44, opacity: started && !settled ? 1 : 0.7 }}
          aria-label={running ? t.pauseT : t.resumeT}
        >
          <span
            className="numeric"
            aria-hidden="true"
            style={{ display: 'block', font: "700 26px 'Space Grotesk',sans-serif", color: !started ? 'var(--sub)' : running ? 'var(--accent)' : 'var(--warn)' }}
          >
            {fmtElapsed(elapsed)}
          </span>
          <span style={{ display: 'block', font: '600 11px Manrope,sans-serif', color: 'var(--sub)' }}>
            {settled ? stateLabel(pod.state) : !started ? t.notStarted : running ? `❚❚ ${t.pauseT}` : `▶ ${t.resumeT}`}
          </span>
        </button>
      </div>

      {started && !running && !settled && (
        <Banner tone="warn" action={{ label: t.resumeT, onClick: onStart }}>
          {pauseReason ? `${t.pausedT} · ${pauseReason}` : t.pausedT}
        </Banner>
      )}

      {/* The pod's place on its route. The bar is one number; the shape of
          the route — its branches and merges — is the graph behind it, drawn
          the same way the "waiting on" sheet draws it. */}
      {(journey.isPending || (journey.data?.length ?? 0) > 0) && (
        <div
          style={{
            minHeight: 44,
            borderRadius: 12,
            padding: '8px 12px',
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {journey.isPending ? (
            <Skeleton height={27} radius={7} style={{ flex: 1 }} />
          ) : (
            <JourneyProgress steps={journey.data ?? []} onOpen={() => setShowRoute(true)} />
          )}
        </div>
      )}

      {upstream ? (
        <WaitingOn pod={pod} journey={journey.data ?? []} loading={journey.isPending} {...(onJumpToStep ? { onJumpTo: onJumpToStep } : {})} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span className="sectionLabel" style={{ marginBottom: 0 }}>
            {t.qualityChecks}
          </span>
          <span style={{ font: "700 12px 'Space Grotesk',sans-serif", color: 'var(--accent)', whiteSpace: 'nowrap' }}>
            {progress.done} / {progress.total}
          </span>
        </div>
      )}

      {upstream ? null : checks.isPending ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={62} radius={14} />
          <Skeleton height={62} radius={14} />
          <Skeleton height={62} radius={14} />
        </div>
      ) : checks.isError ? (
        <Banner tone="danger" action={{ label: t.retry, onClick: () => checks.refetch() }}>
          {(checks.error as Error).message}
        </Banner>
      ) : list.length === 0 ? (
        <div style={{ background: 'var(--surface2)', borderRadius: 13 }}>
          <EmptyState>{t.noChecksConfiguredT}</EmptyState>
        </div>
      ) : (
        <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 0, margin: 0 }}>
          {list.map((c, i) => (
            <CheckCard
              key={c.id}
              check={c}
              index={i}
              size={tablet ? 'board' : 'phone'}
              open={openCheck === c.id}
              read={readSet.includes(c.id)}
              pending={pendingCheckIds.includes(c.id)}
              onToggle={() => toggleCheck(c.id)}
              onRead={() => markRead(c.id)}
              onAction={(a) => runAction(c.id, a)}
              onCapturePhoto={() => onCapturePhoto(c.id)}
              pendingPhoto={pendingPhoto?.checkId === c.id ? pendingPhoto.base64 : undefined}
              onConfirmPhoto={onConfirmPhoto}
            />
          ))}
        </ul>
      )}

      {!settled && !started && !canStart && (
        <p style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', margin: 0 }}>
          {!isActionable(pod.state)
            ? 'This pod is still upstream — chase it if it has stalled.'
            : `You are not signed on at ${pod.workcentreName}. Odoo will not accept the work.`}
        </p>
      )}
      {settled && (
        <p style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', margin: 0 }}>
          {stateLabel(pod.state)} at {pod.workcentreName} — this pod has moved on.
        </p>
      )}
      {progress.ready && started && !settled && nextStep && (
        <p style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', margin: 0 }}>
          {t.wallsTiledReady} <b style={{ color: 'var(--ink)' }}>{nextStep}</b>
        </p>
      )}

      <div style={{ display: 'flex', gap: 10, paddingTop: 4, flexWrap: 'wrap' }}>
        <Button variant="soft" size="md" full={false} onClick={onOpenDocs} icon={<Icon name="file" size={16} />} style={{ padding: '0 14px', minHeight: 48, borderRadius: 13 }}>
          {t.docsShort}
        </Button>
        <Button variant="soft" size="md" full={false} onClick={onOpenIssues} style={{ padding: '0 16px', minHeight: 48, borderRadius: 13 }}>
          {t.issuesOnThisPodT} · {issueCount}
        </Button>
        {/* Raise lives here as well as on More because this is where the
            operator is when something is wrong with the pod, and a form
            pre-filled with it beats one they have to find it for. */}
        <Button variant="dangerGhost" size="md" full={false} onClick={onRaise} icon={<Icon name="hazard" size={16} strokeWidth={2} />} style={{ padding: '0 16px', minHeight: 48, borderRadius: 13 }}>
          {t.raiseOnThisPodT}
        </Button>
        <span style={{ flex: 1, minWidth: 200 }}>
          {settled ? (
            <Button variant="soft" size="md" onClick={() => setShowRoute(true)} icon={<Icon name="list" size={17} />} style={{ minHeight: 48, borderRadius: 13 }}>
              {t.podJourney}
            </Button>
          ) : !started ? (
            !isActionable(pod.state) ? (
              // Nothing to press: the hold-up is drawn above, and each of its
              // rows is the way to go and look at it.
              <Button variant="soft" size="md" disabled icon={<Icon name="list" size={17} />} style={{ minHeight: 48, borderRadius: 13 }}>
                {t.notHereYet}
              </Button>
            ) : (
              // Ink, not accent: this is the tap that puts the operator's name
              // on the clock in Odoo. See `ButtonVariant`.
              <Button variant="ink" size="md" disabled={!canStart} pending={starting} onClick={onStart} icon={<Icon name="play" size={17} />} style={{ minHeight: 48, borderRadius: 13 }}>
                {canStart ? t.startWorkT : t.notYourStationT}
              </Button>
            )
          ) : progress.failed > 0 ? (
            // Blocked, but not inert: the same muted surface a disabled button
            // wears, in the danger colour, and the tap goes to the failed check.
            <Button
              variant="soft"
              size="md"
              onClick={goToFirstFailed}
              icon={<Icon name="cross" size={15} />}
              style={{ minHeight: 48, borderRadius: 13, color: 'var(--danger)', border: '1.5px solid var(--danger)' }}
            >
              {t.checksFailedFixT.replace('{n}', String(progress.failed))}
            </Button>
          ) : (
            <Button variant="ink" size="md" disabled={!progress.ready} onClick={onComplete} style={{ minHeight: 48, borderRadius: 13 }}>
              {progress.ready ? t.completeAdvance : `${progress.done} ${t.routeOfDone} ${progress.total} ${t.checksDoneOfT}`}
            </Button>
          )}
        </span>
      </div>

      <RouteSheet
        open={showRoute}
        pod={pod}
        journey={journey.data ?? []}
        onClose={() => setShowRoute(false)}
        {...(onJumpToStep
          ? {
              onJumpTo: (step) => {
                setShowRoute(false);
                onJumpToStep(step);
              },
            }
          : {})}
      />
    </div>
  );
}
